// src/services/firebase.js
//
// Estrategia de bundle splitting:
// - Estático: firebase/app + firebase/auth → quedan en el bundle inicial (necesarios
//   para que AuthContext escuche el estado de sesión apenas montamos).
// - Dinámico: firebase/firestore, firebase/storage, firebase/messaging,
//   firebase/functions, firebase/analytics → cada uno se emite como chunk separado.
//
// Los dynamic imports arrancan al cargar este módulo y se cachean en promesas.
// Los getters sync (getDb, getStorageInstance, etc.) devuelven la instancia
// hidratada. Los consumers en initial bundle (AuthContext, MaintenanceGate) ya
// hacen `await loadFirestore()` antes de tocar `getDb()` para evitar la race.
// El resto del código corre en rutas lazy: para cuando esos efectos disparan,
// el chunk de Firestore ya está descargado.

import { initializeApp } from "firebase/app";
import { getAuth } from "firebase/auth";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

const firebaseApp = initializeApp(firebaseConfig);

export const auth = getAuth(firebaseApp);

// ──────────────────────────────────────────────────────────────────────
// Firestore (lazy chunk)
// ──────────────────────────────────────────────────────────────────────
let dbInstance = null;
let firestoreModulePromise = null;

const isEmbeddedIframe = () => {
  try {
    return window.self !== window.top;
  } catch {
    // Cross-origin parent throws SecurityError — we're definitely in an iframe
    return true;
  }
};

const ensureFirestore = () => {
  if (!firestoreModulePromise) {
    firestoreModulePromise = import("firebase/firestore").then((mod) => {
      // Safari on iOS aggressively deletes third-party IndexedDB (ITP). When the
      // app runs inside an iframe the origin is treated as third-party and the DB
      // gets wiped between visits, causing Firestore's persistence layer to crash
      // with an unrecoverable INTERNAL ASSERTION FAILED. Use in-memory cache in
      // that context — persistence would be silently lost anyway.
      const localCache = isEmbeddedIframe()
        ? mod.memoryLocalCache()
        : mod.persistentLocalCache();
      dbInstance = mod.initializeFirestore(firebaseApp, { localCache });
      return mod;
    });
  }
  return firestoreModulePromise;
};

export const loadFirestore = async () => {
  await ensureFirestore();
  return dbInstance;
};

export const getDb = () => {
  if (!dbInstance) {
    ensureFirestore();
    throw new Error(
      "[firebase] getDb() invocado antes de que Firestore esté listo. Llamá `await loadFirestore()` antes en el flujo."
    );
  }
  return dbInstance;
};

// ──────────────────────────────────────────────────────────────────────
// Storage (lazy chunk)
// ──────────────────────────────────────────────────────────────────────
let storageInstance = null;
let storageModulePromise = null;

const ensureStorage = () => {
  if (!storageModulePromise) {
    storageModulePromise = import("firebase/storage").then((mod) => {
      storageInstance = mod.getStorage(firebaseApp);
      return mod;
    });
  }
  return storageModulePromise;
};

export const loadStorage = async () => {
  await ensureStorage();
  return storageInstance;
};

export const getStorageInstance = () => {
  if (!storageInstance) {
    ensureStorage();
    throw new Error(
      "[firebase] getStorageInstance() invocado antes de cargar el módulo. Llamá `await loadStorage()` antes."
    );
  }
  return storageInstance;
};

// ──────────────────────────────────────────────────────────────────────
// Functions (lazy chunk)
// ──────────────────────────────────────────────────────────────────────
let functionsInstance = null;
let functionsModulePromise = null;

const ensureFunctions = () => {
  if (!functionsModulePromise) {
    functionsModulePromise = import("firebase/functions").then((mod) => {
      functionsInstance = mod.getFunctions(firebaseApp, "us-central1");
      return mod;
    });
  }
  return functionsModulePromise;
};

export const loadFunctions = async () => {
  const mod = await ensureFunctions();
  return { functions: functionsInstance, httpsCallable: mod.httpsCallable };
};

// `functions` export para retrocompatibilidad con código que ya lo importa.
// Usa Proxy para forzar la inicialización lazy en el primer acceso.
export const functions = new Proxy(
  {},
  {
    get(_t, prop) {
      if (!functionsInstance) {
        ensureFunctions();
        throw new Error(
          "[firebase] functions usado antes de que el módulo esté listo. Llamá `await loadFunctions()` primero."
        );
      }
      const value = functionsInstance[prop];
      return typeof value === "function" ? value.bind(functionsInstance) : value;
    },
  }
);

// ──────────────────────────────────────────────────────────────────────
// Messaging (lazy chunk)
// ──────────────────────────────────────────────────────────────────────
let messagingInstance = null;
let messagingModulePromise = null;

const ensureMessaging = async () => {
  if (!messagingModulePromise) {
    messagingModulePromise = import("firebase/messaging").then(async (mod) => {
      if (await mod.isSupported()) {
        messagingInstance = mod.getMessaging(firebaseApp);
      }
      return mod;
    });
  }
  return messagingModulePromise;
};

export const getMessagingInstance = async () => {
  await ensureMessaging();
  return messagingInstance;
};

export const getToken = async (...args) => {
  const mod = await ensureMessaging();
  return mod.getToken(...args);
};

export const onMessage = (messaging, callback) => {
  let unsub = () => {};
  ensureMessaging().then((mod) => {
    if (messagingInstance) {
      unsub = mod.onMessage(messaging || messagingInstance, callback);
    }
  });
  return () => unsub();
};

export const deleteToken = async (...args) => {
  const mod = await ensureMessaging();
  return mod.deleteToken(...args);
};

// ──────────────────────────────────────────────────────────────────────
// Analytics (lazy chunk)
// ──────────────────────────────────────────────────────────────────────
export const initializeAnalytics = async () => {
  const mod = await import("firebase/analytics");
  if (await mod.isSupported()) {
    mod.getAnalytics(firebaseApp);
  }
};

export default firebaseApp;
